import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import { apiFetch } from '@/api/client';
import { itemsKey, useItemAttributes, useItemCategories } from '@/api/masters';
import { enterKeyScope, useAutoFocusFirstField } from '@/components/enterKeyNavigation';
import { Button, Notice, PageHeading } from '@/components/PageHeading';
import type { Item, ItemVariantAxisOptions, Paged } from '@core/api/masters';
import { categoryAxesPath } from '@core/api/queryPaths';
import { checkStockImport } from '@core/api/stockImport';
import type { Session } from '@core/session/types';
import {
  guessHeaderRow,
  guessMapping,
  layoutKey,
  mappingProblems,
  planImport,
  sheetFrom,
  type CatalogueDesign,
  type FieldKey,
  type PlanInput,
  type WordMap,
} from '@core/units/stockImport';

import { ImportDone, ImportRunning } from './ImportDone';
import {
  CheckCounts,
  CheckStep,
  ColumnsStep,
  FileStep,
  WordsStep,
  type PhotoMode,
} from './ImportSteps';
import {
  importBlocker,
  importLabel,
  keepLayout,
  photoKey,
  readLayouts,
  stem,
  type SavedLayout,
} from './importRules';
import { runImport, type RunProgress, type RunResult } from './importRun';
import { maxRows, readWorkbook, WorkbookProblem, type ReadWorkbook } from './workbook';

type Step = 'file' | 'columns' | 'words' | 'check' | 'run' | 'done';

const stepNames: Record<Exclude<Step, 'run' | 'done'>, string> = {
  file: 'File',
  columns: 'Columns',
  words: 'Words',
  check: 'Check',
};

/* ------------------------------------------------------------- the catalogue */

/** Every design, retired ones too — a retired design's code is still taken. */
function useAllDesigns(branchId: number | undefined) {
  return useQuery({
    queryKey: [...itemsKey, 'all-for-import', { branchId }],
    queryFn: async () => {
      const all: Item[] = [];

      for (let page = 1; ; page += 1) {
        const answer = await apiFetch<Paged<Item>>(
          `/api/item?page=${page}&pageSize=200&includeInactive=true`,
        );

        all.push(...answer.items);

        if (answer.items.length === 0 || all.length >= answer.total) {
          return all;
        }
      }
    },
    enabled: branchId !== undefined,
    staleTime: 30_000,
  });
}

/**
 * Import stock from a spreadsheet (canvas "Stock import from Excel", owner 2026-10-23): any layout
 * the shop already keeps. The person says which column is which and what its words mean, sees every
 * row's fate, and imports. <strong>Nothing is saved until the last step.</strong>
 *
 * <p>
 * The rules are the core's (`units/stockImport.ts`) — this page holds the choices and draws them.
 * The workbook is read on this computer (`workbook.ts`); the run goes through the ordinary writers
 * (`importRun.ts`).
 * </p>
 */
export function StockImportPage({
  session,
  today,
  currency = '₹',
  invalidates,
  onClose,
}: {
  session: Session;
  today: string;
  currency?: string;
  invalidates: readonly (readonly unknown[])[];
  onClose: () => void;
}) {
  const branchId = session.branch?.id;
  const queryClient = useQueryClient();
  const firstField = useAutoFocusFirstField<HTMLDivElement>();

  const [step, setStep] = useState<Step>('file');
  const [book, setBook] = useState<ReadWorkbook | null>(null);
  const [reading, setReading] = useState(false);
  const [readProblem, setReadProblem] = useState<string | null>(null);
  const [sheetIndex, setSheetIndex] = useState(0);
  const [headerRow, setHeaderRow] = useState(0);
  const [photoMode, setPhotoMode] = useState<PhotoMode>('none');
  const [photoFiles, setPhotoFiles] = useState<Map<string, File>>(new Map());
  const [mapping, setMapping] = useState<FieldKey[]>([]);
  const [layoutName, setLayoutName] = useState('');
  const [layoutFound, setLayoutFound] = useState<SavedLayout | null>(null);
  const [defaultCategoryId, setDefaultCategoryId] = useState<number | null>(null);
  const [words, setWords] = useState<WordMap>({});
  const [checked, setChecked] = useState<{
    imported: Set<string>;
    taken: Set<string>;
    takenReferences: Set<string>;
  } | null>(null);
  const [checking, setChecking] = useState(false);
  const [checkProblem, setCheckProblem] = useState<string | null>(null);
  const [delivery, setDelivery] = useState({
    purchasedOn: today,
    supplierVendorId: null as number | null,
    note: '',
  });
  const [progress, setProgress] = useState<RunProgress | null>(null);
  const [result, setResult] = useState<RunResult | null>(null);

  const categories = useItemCategories(branchId);
  const attributes = useItemAttributes(branchId);
  const designs = useAllDesigns(branchId);

  const choosableCategories = useMemo(
    () =>
      (categories.data ?? [])
        .filter((category) => category.isActive)
        .map((category) => ({ id: category.id, name: category.name, code: category.code })),
    [categories.data],
  );
  const attributeList = useMemo(
    () =>
      (attributes.data ?? [])
        .filter((attribute) => attribute.isActive)
        .map((attribute) => ({ id: attribute.id, code: attribute.code, name: attribute.name })),
    [attributes.data],
  );
  const catalogue: CatalogueDesign[] = useMemo(
    () =>
      (designs.data ?? []).map((design) => ({
        id: design.id,
        code: design.code,
        name: design.name,
        categoryId: design.categoryId,
        isActive: design.isActive,
      })),
    [designs.data],
  );

  const readSheet = book?.sheets[sheetIndex];
  const sheet = useMemo(
    () =>
      readSheet
        ? sheetFrom(readSheet.grid, headerRow, (index) =>
            photoMode === 'sheet' ? (readSheet.pictureRows.get(index) ?? null) : null,
          )
        : null,
    [readSheet, headerRow, photoMode],
  );

  const hasPhotoFile = useMemo(
    () =>
      photoMode === 'files'
        ? (name: string) => photoFiles.has(photoKey(name)) || photoFiles.has(stem(name))
        : undefined,
    [photoMode, photoFiles],
  );

  // Each category's axes, for the categories the rows name — asked as they turn up.
  const [axesFor, setAxesFor] = useState<number[]>([]);
  // Combined once per answer, so the plan below is not redone on every keystroke elsewhere.
  const combineAxes = useCallback(
    (results: { data?: ItemVariantAxisOptions[] | undefined; isError: boolean }[]) => {
      const byCategory: Record<number, ItemVariantAxisOptions[] | undefined> = {};

      axesFor.forEach((id, index) => {
        const query = results[index];
        // A category the server cannot answer for is planned with no axes; booking in decides.
        byCategory[id] = query?.data ?? (query?.isError ? [] : undefined);
      });

      return byCategory;
    },
    [axesFor],
  );
  const axesByCategory = useQueries({
    queries: axesFor.map((id) => ({
      queryKey: ['item-category', id, 'axes'],
      queryFn: () => apiFetch<ItemVariantAxisOptions[]>(categoryAxesPath(id)),
      staleTime: 60_000,
    })),
    combine: combineAxes,
  });

  const planInput = useCallback(
    (current: WordMap): PlanInput | null =>
      sheet
        ? {
            sheet,
            mapping,
            words: current,
            designs: catalogue,
            categories: choosableCategories,
            axesByCategory,
            attributeIds: Object.fromEntries(attributeList.map((a) => [a.code, a.id])),
            imported: checked?.imported ?? new Set(),
            takenSerials: checked?.taken ?? new Set(),
            takenReferences: checked?.takenReferences,
            defaultCategoryId,
            hasPhotoFile,
          }
        : null,
    [
      sheet,
      mapping,
      catalogue,
      choosableCategories,
      axesByCategory,
      attributeList,
      checked,
      defaultCategoryId,
      hasPhotoFile,
    ],
  );

  const plan = useMemo(() => {
    const input = planInput(words);

    return input ? planImport(input) : null;
  }, [planInput, words]);

  // New category ids the plan has found: ask for their axes (setState in render is guarded).
  const wanted = plan
    ? [...new Set(plan.rows.map((row) => row.categoryId).filter((id): id is number => id !== null))]
    : [];
  const missing = wanted.filter((id) => !axesFor.includes(id));

  if (missing.length > 0) {
    setAxesFor([...axesFor, ...missing]);
  }

  /* -------------------------------------------------------------- actions */

  const chooseFile = async (file: File) => {
    setReading(true);
    setReadProblem(null);

    try {
      const read = await readWorkbook(file);
      const first = read.sheets.findIndex((candidate) => candidate.grid.length > 0);

      if (first < 0) {
        throw new WorkbookProblem('That file has no rows in it.');
      }

      setBook(read);
      chooseSheet(read, first);
    } catch (error) {
      setReadProblem(
        error instanceof WorkbookProblem ? error.message : 'That file could not be read.',
      );
    } finally {
      setReading(false);
    }
  };

  const chooseSheet = (read: ReadWorkbook, index: number) => {
    const chosen = read.sheets[index]!;
    const header = guessHeaderRow(chosen.grid);

    setSheetIndex(index);
    setHeaderRow(header);
    setPhotoMode(chosen.pictureRows.size > 0 ? 'sheet' : 'none');
    applyHeadings(chosen.grid[header] ?? []);
  };

  const applyHeadings = (cells: readonly string[]) => {
    const headers = sheetFrom([cells.map(String)], 0).headers;
    const saved = readLayouts()[layoutKey(headers)];

    setLayoutFound(saved ?? null);
    setLayoutName(saved?.name ?? '');
    setMapping(
      saved && saved.mapping.length === headers.length
        ? saved.mapping
        : guessMapping(headers, attributeList),
    );
    setWords({});
    setChecked(null);
  };

  const toCheck = async () => {
    if (!plan || !sheet) {
      return;
    }

    setChecking(true);
    setCheckProblem(null);

    try {
      const answer = await checkStockImport({
        fingerprints: plan.rows.map((row) => row.fingerprint),
        serials: [...new Set(plan.rows.flatMap((row) => row.serials))],
        referenceNumbers: [...new Set(plan.rows.flatMap((row) => row.references))],
      });

      setChecked({
        imported: new Set(answer.imported),
        taken: new Set(answer.takenSerials),
        takenReferences: new Set(answer.takenReferenceNumbers ?? []),
      });
      setStep('check');
    } catch (error) {
      setCheckProblem(error instanceof Error ? error.message : 'The server did not answer.');
    } finally {
      setChecking(false);
    }
  };

  const run = async () => {
    if (!plan || !sheet || !book || !readSheet) {
      return;
    }

    if (layoutName.trim()) {
      keepLayout(layoutKey(sheet.headers), {
        name: layoutName.trim(),
        mapping,
        usedOn: today,
      });
    }

    setStep('run');

    const done = await runImport({
      plan,
      words,
      planAgain: (next) => planImport(planInput(next)!),
      source: { fileName: book.fileName, sheetName: readSheet.name, rowsRead: sheet.rows.length },
      delivery,
      photo: (key) =>
        key.startsWith('file:')
          ? (photoFiles.get(photoKey(key.slice(5))) ?? photoFiles.get(stem(key.slice(5))))
          : book.pictures.get(key),
      onProgress: setProgress,
    });

    // The categories' axes too: a size the import added must be known to the next sheet.
    for (const key of [
      ...invalidates,
      itemsKey,
      ['item-attribute'],
      ['item-category'],
      ['stock-import'],
    ]) {
      void queryClient.invalidateQueries({ queryKey: key });
    }

    setResult(done);
    setStep('done');
  };

  /* ------------------------------------------------------------- drawing */

  if (step === 'run') {
    return <ImportRunning progress={progress} />;
  }

  if (step === 'done' && result && sheet && book) {
    return (
      <ImportDone
        session={session}
        result={result}
        sheet={sheet}
        fileName={book.fileName}
        onAgain={() => {
          setResult(null);
          setChecked(null);
          setStep('file');
        }}
        onClose={onClose}
      />
    );
  }

  const order: Step[] = ['file', 'columns', 'words', 'check'];
  const at = order.indexOf(step);
  const columnProblems = mappingProblems(mapping);
  const loadingCatalogue = categories.isLoading || attributes.isLoading || designs.isLoading;
  const tooMany = (sheet?.rows.length ?? 0) > maxRows;

  const next: Record<
    'file' | 'columns' | 'words',
    { label: string; ready: boolean; go: () => void }
  > = {
    file: {
      label: 'Next: match the columns',
      ready: sheet !== null && sheet.rows.length > 0 && !tooMany && !loadingCatalogue,
      go: () => setStep('columns'),
    },
    columns: {
      label: 'Next: match the words',
      ready: columnProblems.length === 0,
      go: () => setStep('words'),
    },
    words: {
      label: checking ? 'Checking the rows…' : 'Next: check',
      ready: !checking && plan !== null,
      go: () => void toCheck(),
    },
  };

  const forward = step === 'file' || step === 'columns' || step === 'words' ? next[step] : null;

  return (
    <div className="flex flex-col gap-3 lg:gap-4" {...enterKeyScope} ref={firstField}>
      <PageHeading
        eyebrow={
          book && sheet
            ? `${book.fileName} · ${readSheet?.name ?? ''} · ${sheet.rows.length} rows`
            : `Item units · ${session.branch?.name ?? 'this branch'}`
        }
        title={headingOf(step)}
        overlap={step === 'check'}
        actions={
          <>
            <Button
              shortcut="back"
              disabled={checking}
              onClick={() => (at === 0 ? onClose() : setStep(order[at - 1]!))}
            >
              {at === 0 ? 'Cancel' : 'Back'}
            </Button>
            {step === 'check' && plan && (
              <Button
                variant="primary"
                shortcut="save"
                data-enter-key-commit
                disabled={importBlocker(session, plan) !== null}
                onClick={() => void run()}
              >
                {importLabel(plan)}
              </Button>
            )}
            {forward && (
              <Button
                variant="primary"
                shortcut="save"
                data-enter-key-commit
                disabled={!forward.ready}
                onClick={forward.go}
              >
                {forward.label}
              </Button>
            )}
          </>
        }
      >
        <ol className="m-0 flex list-none flex-wrap gap-2 ps-0">
          {order.map((name, index) => (
            <li
              key={name}
              className={`list-none rounded-full px-3 py-1.5 text-[13px] ${
                index === at
                  ? 'bg-[#ffffff] font-bold text-(--color-hero)'
                  : index < at
                    ? 'bg-[#ffffff]/20 text-[#ffffff]'
                    : 'bg-[#ffffff]/10 text-(--color-hero-muted)'
              }`}
            >
              {`${index + 1} · ${stepNames[name as keyof typeof stepNames]}${index < at ? ' ✓' : ''}`}
            </li>
          ))}
          <li className="list-none rounded-full bg-[#ffffff]/10 px-3 py-1.5 text-[13px] text-(--color-hero-muted)">
            5 · Import
          </li>
        </ol>
        {step === 'check' && plan && <CheckCounts plan={plan} />}
      </PageHeading>

      {step === 'file' && (
        <FileStep
          session={session}
          book={book}
          reading={reading}
          problem={
            readProblem ??
            (tooMany
              ? `This sheet has more than ${maxRows} rows — split it into smaller sheets.`
              : null)
          }
          sheetIndex={sheetIndex}
          headerRow={headerRow}
          sheet={sheet}
          photoMode={photoMode}
          photoFiles={photoFiles}
          layoutFound={layoutFound}
          onFile={(file) => void chooseFile(file)}
          onSheet={(index) => book && chooseSheet(book, index)}
          onHeaderRow={(row) => {
            setHeaderRow(row);
            applyHeadings(readSheet?.grid[row] ?? []);
          }}
          onPhotoMode={setPhotoMode}
          onPhotoFiles={(files) =>
            setPhotoFiles(
              new Map(
                files.flatMap((file) => [
                  [photoKey(file.name), file] as const,
                  [stem(file.name), file] as const,
                ]),
              ),
            )
          }
        />
      )}

      {step === 'columns' && sheet && (
        <ColumnsStep
          sheet={sheet}
          mapping={mapping}
          attributes={attributeList}
          categories={choosableCategories}
          defaultCategoryId={defaultCategoryId}
          layoutName={layoutName}
          problems={columnProblems}
          onMapping={(next) => {
            setMapping(next);
            setChecked(null);
          }}
          onDefaultCategory={setDefaultCategoryId}
          onLayoutName={setLayoutName}
        />
      )}

      {step === 'words' && sheet && plan && (
        <WordsStep
          session={session}
          sheet={sheet}
          mapping={mapping}
          words={words}
          plan={plan}
          categories={choosableCategories}
          axesByCategory={axesByCategory}
          onWords={(next) => {
            setWords(next);
            setChecked(null);
          }}
        />
      )}

      {checkProblem && step === 'words' && (
        <Notice tone="danger" title="The rows could not be checked">
          {checkProblem}
        </Notice>
      )}

      {step === 'check' && plan && (
        <CheckStep
          session={session}
          plan={plan}
          sheet={sheet!}
          currency={currency}
          branchName={session.branch?.name ?? 'this branch'}
          delivery={delivery}
          pictures={book?.pictures ?? new Map()}
          onDelivery={setDelivery}
        />
      )}
    </div>
  );
}

function headingOf(step: Step): string {
  switch (step) {
    case 'columns':
      return 'Which column is which?';
    case 'words':
      return 'Words in your sheet';
    case 'check':
      return 'Check before importing';
    default:
      return 'Import stock from Excel';
  }
}
